import {
  BillingInterval,
  PaymentStatus,
  SubscriptionTier,
  type CancelSubscription,
  type CancelSubscriptionDto,
  type CreateCheckoutSubscription,
  type CreateCheckoutSubscriptionDto,
  type Price,
  type PriceDto,
  type Referral,
  type ReferralDto,
  type Subscription,
  type SubscriptionDto,
  type UpdateSubscription,
  type UpdateSubscriptionDto,
} from './subscriptions.types';

/**
 * `tier`, `interval` and `payment_status` are untyped strings on the response even
 * though the request bodies constrain them — read defensively instead of casting.
 */
const parseEnum = <T extends Record<string, string>>(values: T, raw: string): T[keyof T] | null =>
  (Object.values(values) as string[]).includes(raw) ? (raw as T[keyof T]) : null;

// Go zero-values empty timestamps to '', which is not a date.
const parseDate = (raw: string): string | null => raw || null;

export const mapDtoToSubscription = ({
  service_id,
  crew_seats,
  payment_status,
  next_payment_at,
  cancel_at_period_end,
  cancel_at,
  canceled_at,
  captain_will_add_owner,
  can_take_over,
  manually_granted,
  tier,
  interval,
  ...subscription
}: SubscriptionDto): Subscription => ({
  ...subscription,
  serviceId: service_id,
  tier: parseEnum(SubscriptionTier, tier),
  interval: parseEnum(BillingInterval, interval),
  crewSeats: crew_seats ?? 0,
  paymentStatus: parseEnum(PaymentStatus, payment_status),
  paymentStatusRaw: payment_status,
  nextPaymentAt: parseDate(next_payment_at),
  cancelAtPeriodEnd: cancel_at_period_end,
  cancelAt: parseDate(cancel_at),
  canceledAt: parseDate(canceled_at),
  captainWillAddOwner: captain_will_add_owner ?? false,
  canTakeOver: can_take_over ?? false,
  manuallyGranted: manually_granted ?? false,
});

export const mapDtoToPrice = ({ id, lookup_key, amount }: PriceDto): Price => ({
  id,
  lookupKey: lookup_key,
  amount: +amount.number,
  currency: amount.currency,
});

/**
 * Go zero-values both strings to '' rather than omitting them, so an empty
 * `referral_link` / `referred_by` means "none", not "not loaded".
 */
export const mapDtoToReferral = ({
  referral_link,
  referrals_count,
  referred_by,
}: ReferralDto): Referral => ({
  link: referral_link || null,
  count: referrals_count ?? 0,
  referredBy: referred_by || null,
});

export const mapCreateCheckoutToDto = ({
  crewSeats,
  willAddOwner,
  ...values
}: CreateCheckoutSubscription): CreateCheckoutSubscriptionDto => ({
  ...values,
  crew_seats: crewSeats,
  ...(willAddOwner !== undefined && { will_add_owner: willAddOwner }),
});

/**
 * Omitted keys keep their current server-side value, so undefined fields are dropped
 * rather than sent as `null`.
 */
export const mapUpdateSubscriptionToDto = ({
  tier,
  interval,
  seats,
  crewSeats,
  willAddOwner,
}: UpdateSubscription): UpdateSubscriptionDto => ({
  ...(tier !== undefined && { tier }),
  ...(interval !== undefined && { interval }),
  ...(seats !== undefined && { seats }),
  ...(crewSeats !== undefined && { crew_seats: crewSeats }),
  // The one field whose omission is not "leave it alone" — see the DTO's note. Callers that
  // can answer the question send the answer they are holding, changed or not.
  ...(willAddOwner !== undefined && { will_add_owner: willAddOwner }),
});

export const mapCancelSubscriptionToDto = ({
  feedback,
  comment,
}: CancelSubscription): CancelSubscriptionDto => ({
  ...(feedback !== undefined && { feedback }),
  ...(comment && { comment }),
});
